import { CheckCircleFilled, CloseCircleFilled, EditOutlined, LoadingOutlined, WarningFilled } from "@ant-design/icons";
import { Alert, Button, Card, Flex, Tag, Typography } from "antd";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { useGetComplianceForNoteQuery } from "../../service/compliance.service";
import { setShowSettingsModal } from "../../slices/appStateSlice";

type Props = {
  audioFileId: number;
};

const ComplianceReview = ({ audioFileId }: Props) => {
  const dispatch = useAppDispatch();
  const { userInfo } = useAppSelector((state) => state.auth);

  const [pollingInterval, setPollingInterval] = useState<number>(2000);
  const { data, isLoading, isError, error } = useGetComplianceForNoteQuery(audioFileId, {
    pollingInterval,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (data?.status && data.status !== "pending") {
      setPollingInterval(0);
    }
  }, [data?.status]);

  // 404 means no compliance run happened for this note — render nothing.
  const status = (error as { status?: number } | undefined)?.status;
  if (isError && status === 404) return null;
  if (!userInfo?.is_compliance_enabled && !data) return null;

  if (isLoading || data?.status === "pending") {
    return (
      <Card
        style={{ marginBottom: 20, background: "#f9fafb" }}
        styles={{
          body: { background: "#fff" },
          header: { backgroundColor: "#fafafc", borderBottom: "2px solid #f0f0f0" },
        }}
        title="Compliance Review"
      >
        <Flex align="center" gap={8} style={{ color: "#6b7280" }}>
          <LoadingOutlined />
          <span>Reviewing this note…</span>
        </Flex>
      </Card>
    );
  }

  if (!data || data.status === "failed") return null;

  const { findings, pass_count, review_count, specialty_used, custom_note_name } = data;

  // Compact success strip when nothing to review.
  if (review_count === 0) {
    return (
      <Alert
        type="success"
        showIcon
        icon={<CheckCircleFilled />}
        message={`Compliance Review: all ${pass_count} rules passed.`}
        style={{ marginBottom: 20 }}
      />
    );
  }

  return (
    <Card
      style={{ marginBottom: 20, background: "#f9fafb" }}
      styles={{
        body: { background: "#fff" },
        header: { backgroundColor: "#fafafc", borderBottom: "2px solid #f0f0f0" },
      }}
      title={
        <Flex justify="space-between" align="center">
          <Flex vertical>
            <span>Compliance Review</span>
            <Typography.Text style={{ fontSize: 12, color: "#6b7280", fontWeight: 400 }}>
              {review_count} {review_count === 1 ? "item" : "items"} to review · {pass_count} others passed
            </Typography.Text>
            {custom_note_name ? (
              <Typography.Text style={{ fontSize: 11, color: "#9ca3af", fontWeight: 400 }}>
                Rules from template: "{custom_note_name}"
              </Typography.Text>
            ) : null}
          </Flex>
        </Flex>
      }
    >
      {specialty_used ? (
        <Alert
          type="warning"
          message={`Specialty noted for additional context: ${specialty_used}`}
          style={{ marginBottom: 16, color: "#B45309", background: "#FFFBEB", border: "1px solid #FDE68A" }}
          action={
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => dispatch(setShowSettingsModal(true))}
              style={{ color: "#B45309" }}
            >
              Edit
            </Button>
          }
        />
      ) : null}

      <Flex vertical>
        {findings.map((finding) => {
          const isFail = finding.judgment === "fail";
          return (
            <Flex
              key={finding.id}
              align="flex-start"
              gap={12}
              style={{ padding: "12px 0", borderBottom: "1px solid #eef0f3" }}
            >
              {isFail ? (
                <CloseCircleFilled style={{ color: "#dc2626", fontSize: 18, marginTop: 2 }} />
              ) : (
                <WarningFilled style={{ color: "#d97706", fontSize: 18, marginTop: 2 }} />
              )}
              <Flex vertical flex={1} gap={4}>
                <Typography.Text strong>{finding.rule_name}</Typography.Text>
                <Typography.Text style={{ color: "#4b5563" }}>{finding.description}</Typography.Text>
              </Flex>
              {finding.category_name || finding.category ? (
                <Tag color="default" style={{ marginRight: 0 }}>
                  {finding.category_name || finding.category?.replaceAll("_", " ")}
                </Tag>
              ) : null}
            </Flex>
          );
        })}
      </Flex>
    </Card>
  );
};

export default ComplianceReview;
